import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { binder, doa, studies, ApiError, type DelegatedStudyUser } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-10 / Sprint-18 (B) / Sprint-20 — AddStaffRowDialog.
 *
 * Replaces the legacy free-text responsibilities textarea with a
 * multi-select checkbox list populated from the DoA responsibilities
 * catalog (the same columns shown in the grid). Selections are stored
 * directly in `ctms_doa_row_delegations` so new rows start with proper
 * grid checkboxes already ticked — no "Σ legacy free-text" chip.
 *
 * The free-text "manual entry" escape hatch was removed — it could never
 * actually complete (a DoA row needs a real staff_user_id, and manual
 * entry never resolved one; submitting from it always failed). Assigning
 * a study delegate directly on the Study Workspace Hub's "Manage staff"
 * modal is the real fix — do that first, then this dialog's picker below
 * will include them.
 */

/**
 * Curated DoA role options — pulled from the system role enum but
 * filtered to roles that realistically appear on a Delegation of Authority
 * log. Sponsor / regulatory personas are excluded (they don't get
 * delegated by the site).
 */
const DOA_ROLE_OPTIONS = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_study_designer',
  'regulatory_specialist',
  'site_pharma',
  'site_admin',
  'monitor',
] as const;

export interface AddStaffRowDialogProps {
  binderId: string;
  versionId: string;
  /** User IDs already present on this DoA version — excluded from the picker. */
  existingUserIds?: string[];
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export function AddStaffRowDialog({
  binderId,
  versionId,
  open,
  onClose,
  onAdded,
  existingUserIds = [],
}: AddStaffRowDialogProps) {
  const qc = useQueryClient();
  const [pickedUserId, setPickedUserId] = useState<string | null>(null);
  const [staffFullName, setStaffFullName] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  const [role, setRole] = useState<string>('');
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');

  // Resolve study_id from binder (single round-trip, cached for 5 min).
  const binderQuery = useQuery({
    queryKey: ['binder', 'get', binderId],
    queryFn: () => binder.get(binderId),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const studyId = binderQuery.data?.binder.study_id ?? null;

  // Pull active delegations for the study (the picker's data source).
  const delegationsQuery = useQuery({
    queryKey: ['study', studyId, 'delegated-users'],
    queryFn: () => studies.listDelegatedUsers(studyId!),
    enabled: !!studyId && open,
    staleTime: 30_000,
  });

  // Load responsibilities catalog from the grid (same source as the grid columns).
  const gridQuery = useQuery({
    queryKey: ['doa-grid', binderId],
    queryFn: () => doa.getGrid(binderId),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const responsibilityCatalog = gridQuery.data?.responsibilities ?? [];

  const delegatedUsers: DelegatedStudyUser[] = delegationsQuery.data?.users ?? [];

  // Reset whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setPickedUserId(null);
      setStaffFullName('');
      setStaffEmail('');
      setRole('');
      setSelectedCodes(new Set());
      setEffectiveFrom('');
      setEffectiveTo('');
    }
  }, [open, versionId]);

  function toggleCode(code: string) {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  // Selecting a user from the picker auto-fills the form.
  function pickUser(userId: string) {
    setPickedUserId(userId);
    const u = delegatedUsers.find((d) => d.user_id === userId);
    if (u) {
      setStaffFullName(u.full_name);
      setStaffEmail(u.email);
      setRole(u.role);
      // Default effective_from to the delegation's start date.
      if (!effectiveFrom) {
        // delegation effective_from is ISO timestamp — slice to YYYY-MM-DD for <input type="date">
        setEffectiveFrom(u.effective_from.slice(0, 10));
      }
    }
  }

  const trimmedName = staffFullName.trim();
  const trimmedEmail = staffEmail.trim();
  const trimmedRole = role.trim();

  const isValid = useMemo(
    () =>
      !!pickedUserId &&
      trimmedRole.length > 0 &&
      selectedCodes.size > 0 &&
      effectiveFrom.length > 0,
    [pickedUserId, trimmedRole, selectedCodes, effectiveFrom],
  );

  /**
   * BE schema requires:
   *   - staff_user_id (non-empty string) — the resolved user_id
   *   - effective_from / effective_to as ISO 8601 datetimes (z.string().datetime())
   *
   * `<input type="date">` produces "YYYY-MM-DD" — the BE rejects that with a
   * 400. We pad to a full UTC datetime (00:00:00Z for the start, 23:59:59Z for
   * the end, mirroring "delegation effective for the entire day").
   */
  const toIsoStart = (d: string) => (d ? new Date(`${d}T00:00:00Z`).toISOString() : '');
  const toIsoEnd = (d: string) => (d ? new Date(`${d}T23:59:59Z`).toISOString() : '');

  const addRow = useMutation({
    mutationFn: () => {
      if (!pickedUserId) throw new Error('Pick a delegated staff member first.');
      return doa.addRow(binderId, versionId, {
        staff_user_id: pickedUserId,
        staff_full_name: trimmedName,
        staff_email: trimmedEmail,
        role: trimmedRole,
        responsibility_codes: [...selectedCodes],
        effective_from: toIsoStart(effectiveFrom),
        ...(effectiveTo ? { effective_to: toIsoEnd(effectiveTo) } : {}),
      });
    },
    onSuccess: () => {
      toast.success(`Added ${trimmedName} to the DoA`);
      qc.invalidateQueries({
        queryKey: ['binder', binderId, 'doa', 'version', versionId],
      });
      qc.invalidateQueries({ queryKey: ['doa-grid', binderId] });
      onAdded();
      onClose();
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to add staff member'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid || addRow.isPending) return;
    addRow.mutate();
  }

  if (!open) return null;

  // Exclude users who are already on this DoA version.
  const userOptions = delegatedUsers.filter((u) => !existingUserIds.includes(u.user_id));

  // Show a full-dialog spinner while binder/delegations are loading so the
  // form never renders with an empty/misleading state.
  const isDataLoading = binderQuery.isLoading || delegationsQuery.isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !addRow.isPending) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="card w-full max-w-lg space-y-4 p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Add staff to DoA
            </h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Pick from the staff already delegated to this study.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={addRow.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Full-dialog loader while binder / delegations data is in-flight */}
        {isDataLoading && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading study staff…
          </div>
        )}

        {/* Pick from delegated users */}
        {!isDataLoading && (
          <>
            <div>
              <label className="label text-xs" htmlFor="doa-staff-picker">
                Delegated staff *
              </label>
              {binderQuery.isLoading || delegationsQuery.isLoading ? (
                <div className="input flex items-center gap-2 text-xs text-ink-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading study delegations…
                </div>
              ) : binderQuery.isError || delegationsQuery.isError || !studyId ? (
                // A failed binder lookup leaves the staff query disabled; without
                // this branch the dialog fell through to "No staff are currently
                // delegated", which is wrong. Show the server's reason instead
                // (e.g. "Your role on this study (…) can't do this.").
                <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">
                  Couldn&apos;t load the study&apos;s delegated staff
                  {(() => {
                    const err = binderQuery.error ?? delegationsQuery.error;
                    return err instanceof ApiError ? `: ${err.message}` : '.';
                  })()}
                </div>
              ) : userOptions.length === 0 && delegatedUsers.length === 0 ? (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  No staff are currently delegated to this study. Assign staff from the study's
                  workspace page ("Manage staff") first.
                </div>
              ) : userOptions.length === 0 ? (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                  All delegated staff are already on this DoA version.
                </div>
              ) : (
                <div className="relative">
                  <select
                    id="doa-staff-picker"
                    className="input w-full appearance-none pr-8"
                    value={pickedUserId ?? ''}
                    disabled={addRow.isPending}
                    onChange={(e) => pickUser(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Pick a delegated staff member…
                    </option>
                    {userOptions.map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.full_name} — {humanRole(u.role)} ({u.email})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-ink-muted" />
                </div>
              )}
            </div>

            {/* Role override + responsibilities + dates only shown after a user is picked */}
            {pickedUserId && (
              <div>
                <label className="label text-xs" htmlFor="doa-role">
                  Role on the DoA *
                </label>
                <div className="relative">
                  <select
                    id="doa-role"
                    className="input w-full appearance-none pr-8"
                    value={role}
                    disabled={addRow.isPending}
                    onChange={(e) => setRole(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Pick a role…
                    </option>
                    {DOA_ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {humanRole(r)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-ink-muted" />
                </div>
                <p className="mt-1 text-[11px] text-ink-muted">
                  Defaults to the role assigned during onboarding — override if this DoA captures
                  them in a different capacity.
                </p>
              </div>
            )}
          </>
        )}

        {/* Responsibilities + dates — shown once a delegated user is picked */}
        {pickedUserId && (
          <>
            <div>
              <label className="label text-xs">
                Responsibilities *
                {selectedCodes.size > 0 && (
                  <span className="ml-1.5 text-ink-muted font-normal">
                    ({selectedCodes.size} selected)
                  </span>
                )}
              </label>
              {gridQuery.isLoading ? (
                <div className="flex items-center gap-2 text-xs text-ink-muted py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading responsibilities…
                </div>
              ) : responsibilityCatalog.length === 0 ? (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  No responsibilities configured. Ask a platform admin to set up the DoA catalog.
                </div>
              ) : (
                <div className="rounded border border-surface-border divide-y divide-surface-border max-h-52 overflow-y-auto">
                  {responsibilityCatalog.map((resp) => (
                    <label
                      key={resp.code}
                      className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-muted/50 select-none"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0 accent-brand-600"
                        checked={selectedCodes.has(resp.code)}
                        disabled={addRow.isPending}
                        onChange={() => toggleCode(resp.code)}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-ink-primary leading-tight">
                          {resp.label}
                        </span>
                        {resp.definition && (
                          <span className="block mt-0.5 text-[11px] text-ink-muted leading-tight line-clamp-2">
                            {resp.definition}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {selectedCodes.size === 0 && responsibilityCatalog.length > 0 && (
                <p className="mt-1 text-[11px] text-ink-muted">
                  Select at least one responsibility to delegate.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-xs" htmlFor="doa-eff-from">
                  Effective from *
                </label>
                <input
                  id="doa-eff-from"
                  className="input"
                  type="date"
                  value={effectiveFrom}
                  disabled={addRow.isPending}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label text-xs" htmlFor="doa-eff-to">
                  Effective to
                </label>
                <input
                  id="doa-eff-to"
                  className="input"
                  type="date"
                  value={effectiveTo}
                  disabled={addRow.isPending}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                />
                <p className="mt-1 text-[11px] text-ink-muted">Blank = open-ended.</p>
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-surface-border pt-3">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={addRow.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || addRow.isPending}>
            {addRow.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Add staff member
          </button>
        </div>
      </form>
    </div>
  );
}
